import React, { useState, useEffect } from 'react';
import MaterialTable from 'material-table';
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { api } from 'common';
import { useTranslation } from "react-i18next";
import { colors } from '../components/Theme/WebTheme';
import { WEB_MAIN_COLOR, WEB_SECONDORY_COLOR, FONT_FAMILY } from "../common/sharedFunctions";
import { ThemeProvider } from '@mui/material/styles';
import theme from "styles/tableStyle";
import TableStyle from '../components/Table/Style';
import localization from '../components/Table/Localization';
import { getLangKey } from 'common/src/other/getLangKey';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import InventoryIcon from '@mui/icons-material/Inventory2Outlined';
import TuneIcon from '@mui/icons-material/TuneOutlined';
import CancelScheduleSendIcon from '@mui/icons-material/CancelScheduleSendOutlined';
import Chip from '@mui/material/Chip';

export default function ParcelTypes() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const {
    editDeliveryConfigItem,
    convertLanguage,
    fetchDeliveryConfig
  } = api;

  const [tabIndex, setTabIndex] = useState(0);
  const [selectedRow, setSelectedRow] = useState(null);

  const settings = useSelector(state => state.settingsdata.settings);
  const deliveryconfig = useSelector(state => state.deliveryconfig);
  const auth = useSelector(state => state.auth);
  const dispatch = useDispatch();

  const MAIN_COLOR = WEB_MAIN_COLOR(settings?.appCat);
  const SECONDORY_COLOR = WEB_SECONDORY_COLOR(settings?.appCat);

  useEffect(() => {
    dispatch(fetchDeliveryConfig());
  }, [dispatch, fetchDeliveryConfig]);

  const parcelColumns = [
    {
      title: t('description', { defaultValue: 'Description' }),
      field: 'description',
      render: rowData => <span>{t(getLangKey(rowData.description))}</span>,
    },
    {
      title: t('amount', { defaultValue: 'Amount' }),
      field: 'amount',
      type: 'numeric',
      render: rowData => (
        <span>
          {settings?.symbol} {parseFloat(rowData.amount || 0).toFixed(settings?.decimal || 2)}
        </span>
      ),
    }
  ];

  const optionColumns = [
    {
      title: t('description', { defaultValue: 'Description' }),
      field: 'description',
      render: rowData => <span>{t(getLangKey(rowData.description))}</span>,
    },
    {
      title: t('amount', { defaultValue: 'Amount' }),
      field: 'amount',
      type: 'numeric',
      render: rowData => (
        <span>
          {settings?.symbol} {parseFloat(rowData.amount || 0).toFixed(settings?.decimal || 2)}
        </span>
      ),
    }
  ];

  const cancelSlabColumns = [
    {
      title: t('minsDelayed', { defaultValue: 'Delay Minutes (minsDelayed)' }),
      field: 'minsDelayed',
      type: 'numeric',
      render: rowData => <span>{rowData.minsDelayed} mins</span>,
    },
    {
      title: t('amount', { defaultValue: 'Cancellation Fee' }),
      field: 'amount',
      type: 'numeric',
      render: rowData => (
        <span>
          {settings?.symbol} {parseFloat(rowData.amount || 0).toFixed(settings?.decimal || 2)}
        </span>
      ),
    }
  ];

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
    setSelectedRow(null);
  };

  const getActiveData = () => {
    if (tabIndex === 0) return deliveryconfig.parcelTypes || [];
    if (tabIndex === 1) return deliveryconfig.options || [];
    if (tabIndex === 2) return deliveryconfig.cancelSlabs || [];
    return [];
  };

  const getActiveType = () => {
    if (tabIndex === 0) return 'parcelTypes';
    if (tabIndex === 1) return 'options';
    if (tabIndex === 2) return 'cancelSlabs';
    return 'parcelTypes';
  };

  const getActiveColumns = () => {
    if (tabIndex === 0) return parcelColumns;
    if (tabIndex === 1) return optionColumns;
    if (tabIndex === 2) return cancelSlabColumns;
    return parcelColumns;
  };

  const getActiveTitle = () => {
    if (tabIndex === 0) return t('parcel_types', { defaultValue: 'Parcel Types' });
    if (tabIndex === 1) return t('options', { defaultValue: 'Delivery Options' });
    if (tabIndex === 2) return t('cancelSlab', { defaultValue: 'Cancellation Slabs' });
    return '';
  };

  return deliveryconfig.loading ? (
    <CircularLoading />
  ) : (
    <Box sx={{ width: '100%', p: { xs: 1, md: 3 } }}>
      <Card
        sx={{
          p: 2,
          mb: 3,
          borderRadius: '16px',
          boxShadow: `0px 3px 10px rgba(0,0,0,0.08)`,
          background: 'linear-gradient(135deg, #ffffff 0%, #f9fbfd 100%)',
          border: '1px solid #eef2f6'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <div>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', fontFamily: FONT_FAMILY }}>
              {t('delivery_pricing_config', { defaultValue: 'Unified Pricing & Slab Configuration' })}
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5, fontFamily: FONT_FAMILY }}>
              {t('delivery_pricing_config_desc', { defaultValue: 'Manage master Parcel Types, Extra Options, and Cancellation Slabs used across all vehicle types.' })}
            </Typography>
          </div>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              icon={<InventoryIcon fontSize="small" />}
              label={`${deliveryconfig.parcelTypes?.length || 0} Parcel Types`}
              variant={tabIndex === 0 ? "filled" : "outlined"}
              color="primary"
              onClick={() => setTabIndex(0)}
              sx={{ cursor: 'pointer', fontWeight: 600 }}
            />
            <Chip
              icon={<TuneIcon fontSize="small" />}
              label={`${deliveryconfig.options?.length || 0} Options`}
              variant={tabIndex === 1 ? "filled" : "outlined"}
              color="primary"
              onClick={() => setTabIndex(1)}
              sx={{ cursor: 'pointer', fontWeight: 600 }}
            />
            <Chip
              icon={<CancelScheduleSendIcon fontSize="small" />}
              label={`${deliveryconfig.cancelSlabs?.length || 0} Slabs`}
              variant={tabIndex === 2 ? "filled" : "outlined"}
              color="primary"
              onClick={() => setTabIndex(2)}
              sx={{ cursor: 'pointer', fontWeight: 600 }}
            />
          </Box>
        </Box>

        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              fontWeight: 600,
              fontSize: '0.95rem',
              textTransform: 'none',
              fontFamily: FONT_FAMILY,
              minHeight: 48,
            }
          }}
        >
          <Tab icon={<InventoryIcon />} iconPosition="start" label={t('parcel_types', { defaultValue: 'Parcel Types' })} />
          <Tab icon={<TuneIcon />} iconPosition="start" label={t('options', { defaultValue: 'Delivery Options' })} />
          <Tab icon={<CancelScheduleSendIcon />} iconPosition="start" label={t('cancelSlab', { defaultValue: 'Cancellation Slabs' })} />
        </Tabs>
      </Card>

      <ThemeProvider theme={theme}>
        <MaterialTable
          title={getActiveTitle()}
          columns={getActiveColumns()}
          style={{
            direction: isRTL === "rtl" ? "rtl" : "ltr",
            borderRadius: "16px",
            padding: "16px",
            boxShadow: `0px 2px 8px ${SECONDORY_COLOR}`,
          }}
          data={getActiveData()}
          onRowClick={(evt, row) => setSelectedRow(row.tableData?.id)}
          options={{
            exportButton: true,
            rowStyle: (rowData) => ({
              backgroundColor:
                selectedRow === rowData.tableData?.id ? colors.ROW_SELECTED : colors.WHITE,
            }),
            ...TableStyle(settings)
          }}
          localization={localization(t)}
          editable={settings?.AllowCriticalEditsAdmin ? {
            onRowAdd: newData =>
              new Promise((resolve, reject) => {
                setTimeout(async () => {
                  const currentType = getActiveType();
                  if (currentType === 'cancelSlabs') {
                    if (newData.minsDelayed === undefined || newData.amount === undefined) {
                      alert(t('no_details_error', { defaultValue: 'Please provide all details' }));
                      reject();
                      return;
                    }
                  } else {
                    if (!newData.description || newData.amount === undefined) {
                      alert(t('no_details_error', { defaultValue: 'Please provide all details' }));
                      reject();
                      return;
                    }
                    if (newData.description) {
                      await convertLanguage(newData.description, auth?.profile?.lang?.langLocale ? auth.profile.lang.langLocale : null);
                    }
                  }
                  dispatch(editDeliveryConfigItem(currentType, newData, "Add"));
                  resolve();
                }, 400);
              }),
            onRowUpdate: (newData, oldData) =>
              new Promise((resolve, reject) => {
                setTimeout(async () => {
                  const currentType = getActiveType();
                  if (currentType === 'cancelSlabs') {
                    if (newData.minsDelayed === undefined || newData.amount === undefined) {
                      alert(t('no_details_error', { defaultValue: 'Please provide all details' }));
                      reject();
                      return;
                    }
                  } else {
                    if (!newData.description || newData.amount === undefined) {
                      alert(t('no_details_error', { defaultValue: 'Please provide all details' }));
                      reject();
                      return;
                    }
                    if (newData.description && newData.description !== oldData.description) {
                      await convertLanguage(newData.description, auth?.profile?.lang?.langLocale ? auth.profile.lang.langLocale : null);
                    }
                  }
                  dispatch(editDeliveryConfigItem(currentType, newData, "Update"));
                  resolve();
                }, 400);
              }),
            onRowDelete: oldData =>
              new Promise(resolve => {
                setTimeout(() => {
                  const currentType = getActiveType();
                  dispatch(editDeliveryConfigItem(currentType, oldData, "Delete"));
                  resolve();
                }, 400);
              }),
          } : null}
        />
      </ThemeProvider>
    </Box>
  );
}
